import type { PoseId } from '@/content/articulation';
import type { ShotId } from '@/ui/tongue3d/TongueScene';

/**
 * The Tongue Lab's activities (docs/design/articulation-spec.md for what each shape shows). A
 * returning learner gets a different one each visit (`nextActivity`), so the lesson does not
 * repeat itself; the first visit watches the guided steps. After each one the child picks what
 * comes next (`offers`).
 */
export type Activity = 'watch' | 'build' | 'quiz' | 'fix' | 'drag' | 'spin';

/** The visit order: watching first, then doing, alternating kinds. */
export const ORDER: readonly Activity[] = ['watch', 'build', 'quiz', 'fix', 'drag', 'spin'];

/** What each activity is called when offered, and the words that pick it by voice. */
export const ACTIVITY_NAMES: Readonly<Record<Activity, { label: string; says: string[] }>> = {
  watch: { label: 'Watch the r', says: ['watch', 'show me', 'movie'] },
  build: { label: 'Build the r', says: ['build', 'step', 'make'] },
  quiz: { label: 'Which is r?', says: ['quiz', 'which', 'guess'] },
  fix: { label: 'Spot the mistake', says: ['mistake', 'spot', 'fix', 'wrong'] },
  drag: { label: 'Move the tongue', says: ['move', 'drag', 'push', 'myself'] },
  spin: { label: 'Spin and look', says: ['spin', 'turn', 'look', 'around'] },
};

/** Two activities to choose from after `done`: the next ones in the visit order. */
export function offers(done: Activity): [Activity, Activity] {
  const i = ORDER.indexOf(done);
  const at = (k: number) => ORDER[(i + k) % ORDER.length] ?? 'watch';
  return [at(1), at(2)];
}

/** One step of the guided demo: where the mouth moves, from where the camera looks, what is said. */
export interface Step {
  /** The shape, as a blend from rest toward `pose` (0 rest, 1 the pose). */
  pose: PoseId;
  amount: number;
  shot: ShotId;
  caption: string;
  /** How long the step holds after the move (ms). */
  holdMs: number;
  /** Mark the narrowest point under the roof. */
  gap?: boolean;
  /** Turn the camera round the mouth and back during the hold. */
  tour?: boolean;
}

/** Relaxed to a bunched /r/, one part at a time (the spec's elements of /r/, in order). */
export const WATCH: readonly Step[] = [
  {
    pose: 'rBunched',
    amount: 0,
    shot: 'side',
    caption: 'A relaxed tongue: the tip rests behind the bottom front teeth.',
    holdMs: 2600,
  },
  {
    pose: 'rBunched',
    amount: 0.5,
    shot: 'side',
    caption: 'For a strong r, the middle of the tongue lifts up…',
    holdMs: 2200,
  },
  {
    pose: 'rBunched',
    amount: 1,
    shot: 'side',
    caption: '…close to the roof of the mouth, but not touching it.',
    holdMs: 2800,
    gap: true,
  },
  {
    pose: 'rBunched',
    amount: 1,
    shot: 'threeQuarter',
    caption: 'The sides of the tongue press against the back teeth.',
    holdMs: 1000,
    tour: true,
  },
  {
    pose: 'rBunched',
    amount: 1,
    shot: 'side',
    caption: 'The tip stays low, and the lips round just a little.',
    holdMs: 2800,
  },
];

/** The quiz's look-alike shapes for the strong r, and what to say about each. */
export const LOOKALIKES: Readonly<Record<'w' | 'l', { name: string; explain: string }>> = {
  w: {
    name: 'a w',
    explain:
      'That one is a w: the back of the tongue goes up and the lips round tightly, like "oo".',
  },
  l: {
    name: 'an l',
    explain: 'That one is an l: the tip of the tongue touches the bump behind the top teeth.',
  },
};

const VISITS = 'foe-names.tonguelab.v1';

/** The activity for this visit: watch first, then the others in turn, changing every visit. */
export function nextActivity(storage: Pick<Storage, 'getItem' | 'setItem'> | undefined): Activity {
  let visits = 0;
  try {
    visits = Number(storage?.getItem(VISITS) ?? '0') || 0;
    storage?.setItem(VISITS, String(visits + 1));
  } catch {
    // storage blocked: every visit is a first one
  }
  return ORDER[visits % ORDER.length] ?? 'watch';
}
